/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { BlocksContent } from "@strapi/blocks-react-renderer";
import { Memoize } from "typescript-memoize";
import { NewRelicDecoratedError } from "utilities/noticeError";

export enum PublicationState {
  preview = "preview",
  live = "live",
}

export enum SortBy {
  updatedAtDesc = "updatedAt:desc",
}

export interface CmsResponse<A> {
  data: A[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface CmsResource<A> {
  id: number;
  attributes: A & {
    createdAt: string; // ISO 8601
    updatedAt: string; // ISO 8601
    publishedAt: string | null;
  };
}

export interface CmsImage {
  data: CmsResource<CmsImageAttributes>;
}

export interface CmsImageAttributes {
  url: string;
  width: number;
  height: number;
  caption: string | null;
}

export interface CmsCodeAttributes {
  type: string;
  url?: string;
  caption?: string;
}

export interface CmsPlayerAttributes {
  url: string;
  caption?: string;
}

export interface GuidePageAttributes {
  Title: string;
  Subtitle: string;
  Content: BlocksContent;
  Hero: CmsImage;
  pageTitle: string;
  metaDescription: string;
  slug: string;
  shortTitle: string;
  locale: string;
  localizations: {
    data: CmsResource<GuidePageAttributes>[];
  };
}

export type GuidePage = CmsResource<GuidePageAttributes>;

export class CmsClient {
  private readonly baseUrl: string;
  private readonly adobeIOApiKey: string;

  constructor(baseUrl: string, adobeIOApiKey: string) {
    this.baseUrl = baseUrl;
    this.adobeIOApiKey = adobeIOApiKey;
  }

  @Memoize()
  async getAllGuidePages(params: {
    locale: string;
    sortBy?: SortBy;
    count?: number;
  }): Promise<GuidePage[]> {
    const pages: GuidePage[] = [];
    let page = 1;
    let hasMore = true;
    while (hasMore) {
      const response = await this.getGuidePages(
        PublicationState.live,
        params.locale,
        params.sortBy,
        page
      );
      pages.push(...response.data);
      hasMore =
        response.meta.pagination.pageCount > page &&
        (params.count === undefined || pages.length < params.count);
      page++;
    }

    if (params.count !== undefined) {
      return pages.slice(0, params.count);
    } else {
      return pages;
    }
  }

  @Memoize()
  async getGuidePage(slug: string): Promise<GuidePage | undefined> {
    const [livePage, previewPage] = await Promise.all([
      this.getGuidePageInState(PublicationState.live, slug),
      this.getGuidePageInState(PublicationState.preview, slug),
    ]);

    return livePage ?? previewPage;
  }

  private async getGuidePages(
    publicationState: PublicationState,
    locale: string,
    sortBy: SortBy = SortBy.updatedAtDesc,
    page: number = 1
  ): Promise<CmsResponse<GuidePage>> {
    const response = await this.baseFetch(
      `guide-pages?publicationState=${publicationState}&sort=${sortBy}&locale=${locale}&populate=*&pagination[page]=${page}`
    );
    return await response.json();
  }

  private async getGuidePageInState(
    publicationState: PublicationState,
    slug: string
  ): Promise<GuidePage | undefined> {
    const response = await this.baseFetch(
      `guide-pages?publicationState=${publicationState}&locale=all&populate=*&filters[slug][$eq]=${encodeURI(
        slug
      )}`
    );
    const page: CmsResponse<GuidePage> = await response.json();
    return page.data[0];
  }

  private baseFetch: typeof fetch = async (
    url: string,
    requestInfo?: RequestInit
  ): Promise<Response> => {
    const response = await fetch(`${this.baseUrl}/${url}`, {
      ...requestInfo,
      headers: {
        ["X-Api-Key"]: this.adobeIOApiKey,
      },
    });
    if (response.ok) {
      return response;
    } else {
      throw new CmsError("unexpected cms response", {
        status: response.status,
        url: url,
        method: requestInfo?.method ?? "GET",
      });
    }
  };
}

class CmsError extends NewRelicDecoratedError {
  constructor(
    error: Error | string,
    customAttributes: Record<string, newrelic.SimpleType> = {}
  ) {
    super(error, customAttributes);
    this.name = "CmsError;";
  }
}

export namespace CmsApi {
  export const instance = new CmsClient(
    `${process.env.PHONOS_GATEWAY_URL!}/api/v1/cms`,
    process.env.ADOBE_IO_API_KEY!
  );
}
